import { FileText, Globe, Pencil, Sparkles, type LucideIcon } from 'lucide-react';
import {
  AI_SUGGESTED_TAG_LABEL,
  BRAND_SOURCE_TAG_LABELS,
  canonicalSources,
  type BrandSourceTag,
} from '@mg/shared';
import { cn } from '../../../lib/cn';
import { hasSourceTags } from './fieldParts';

const SOURCE_ICONS: Record<BrandSourceTag, LucideIcon> = {
  brandDocument: FileText,
  brandWebsite: Globe,
  manualEntry: Pencil,
};

const CHIP = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs';

/**
 * Where a field's content came from (spec:103-111): Brand Document, Brand website and Manual Entry, in
 * that order, then "AI suggested" in its own colour with a sparkle (spec:124). Informational only. A
 * field with no content passes no sources and shows nothing (spec:109). Each tag has an icon and words,
 * so none relies on colour.
 */
export function SourceTags({
  id,
  sources,
  aiSuggested = false,
  className,
}: {
  /** For the control's aria-describedby (fieldPartIds(controlId).sources). */
  id?: string;
  sources: readonly BrandSourceTag[];
  aiSuggested?: boolean;
  className?: string;
}) {
  if (!hasSourceTags(sources, aiSuggested)) return null;
  return (
    <ul id={id} aria-label="Content sources" className={cn('flex flex-wrap gap-1', className)}>
      {canonicalSources(sources).map((tag) => {
        const Icon = SOURCE_ICONS[tag];
        return (
          <li key={tag}>
            <span className={cn(CHIP, 'bg-chip text-ink-label')}>
              <Icon className="h-3 w-3 shrink-0" aria-hidden />
              {BRAND_SOURCE_TAG_LABELS[tag]}
            </span>
          </li>
        );
      })}
      {aiSuggested && (
        <li>
          <span className={cn(CHIP, 'bg-ai-tint text-ai')}>
            <Sparkles className="h-3 w-3 shrink-0" aria-hidden />
            {AI_SUGGESTED_TAG_LABEL}
          </span>
        </li>
      )}
    </ul>
  );
}
